import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    HostBinding,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewContainerRef
} from '@angular/core';
import * as _ from 'lodash-es';
import {Observable} from 'rxjs';
import {filter, takeUntil} from 'rxjs/operators';
import {destroy$} from '../../../../components/destroyable';
import {Cell, CellDisplayInfo} from '../../../../components/layout/cell';
import {Widget} from '../../../../components/layout/widget/widget';
import {LayoutSharedService, LayoutUpdateEvent, LayoutUpdateReason} from '../layout-shared-service';
import {CellSizeObserverService} from './cell-size-observer.service';

const LOCKED_CLASS = '-locked';
const MOVING_CLASS = '-moving';
const SETTINGS_OPENED_CLASS = '-settings-opened';

@Component({
    selector: 'cell',
    templateUrl: './cell.component.html',
    styleUrls: ['./cell.component.scss'],
    providers: [
        CellSizeObserverService
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CellComponent implements OnInit, OnDestroy {

    @Input('cell')
    public cell: Cell;

    public readonly destroy$ = destroy$();

    public isShadowed: boolean = false; // Used in template to show overlay.

    public onResize$: Observable<LayoutUpdateEvent> = this.cellSizeObserverService.resize$;

    @HostBinding(`class.${MOVING_CLASS}`)
    public isMoving: boolean = false; // To be changed from outside.

    @HostBinding(`class.${SETTINGS_OPENED_CLASS}`)
    public settingsOpenedForWidget: Widget = null; // To be changed from outside.

    public isCellBodyHovered: boolean;

    constructor(
        public cellElementRef: ElementRef,
        public viewContainerRef: ViewContainerRef,
        private renderer: Renderer2,
        private injector: Injector,
        private cellSizeObserverService: CellSizeObserverService,
        private layoutSharedService: LayoutSharedService
    ) {}

    public ngOnInit(): void {
        this.cell.display.cellComponent = this;

        this.listenToLayoutUpdates();
        this.update();
    }

    public ngOnDestroy(): void {
        this.cell.display.cellComponent = null;
    }

    private update(): void {
        const displayData: CellDisplayInfo = this.cell.display;

        if (displayData && displayData.pos && displayData.size) {
            const [left, top] = displayData.pos;
            const [width, height] = displayData.size;

            this.setStyles({
                left:   left + 'px',
                top:    top + 'px',
                width:  width + 'px',
                height: height + 'px'
            });

            this.cellSizeObserverService.resizeSubject$.next();
        }
    }

    private setIsShadowed(state: boolean): void {
        this.isShadowed = state;
    }

    private setLocking(state: boolean): void {
        this.toggleClass(LOCKED_CLASS, state);
    }

    private setStyles(stylesMap: {[prop: string]: any}): void {
        const renderer = this.renderer;
        const element  = this.cellElementRef.nativeElement;

        _.forEach(stylesMap, (value, key) =>
            renderer.setStyle(element, key, value)
        );
    }

    private toggleClass(className: string, state: boolean): void {
        const renderer = this.renderer;
        const element  = this.cellElementRef.nativeElement;

        if (state) {
            renderer.addClass(element, className);
        } else {
            renderer.removeClass(element, className);
        }
    }

    private listenToLayoutUpdates(): void {
        const cell: Cell = this.cell;

        this.layoutSharedService.update$.pipe(
            takeUntil(this.destroy$),
            filter(event => !event || !event.cells || event.cells.includes(cell))
        ).subscribe((event: LayoutUpdateEvent) => {
                switch (event.reason) {
                    case LayoutUpdateReason.RESIZE:
                        this.update();
                        break;
                    case LayoutUpdateReason.LOCK:
                        this.setLocking(true);
                        break;
                    case LayoutUpdateReason.UNLOCK:
                        this.setLocking(false);
                        break;
                    case LayoutUpdateReason.BLUR:
                        this.setIsShadowed(true);
                        break;
                    case LayoutUpdateReason.UNBLUR:
                        this.setIsShadowed(false);
                        break;
                }
            });
    }

}
